import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';
import jsPDF from 'jspdf';
import { isIndia } from '../../../utils/countries';
import { getCurrencySymbol, getCurrencyName, countryCurrencies } from '../../../utils/currencies';
import { validateFileUpload } from './FormValidation';

const CategorizationStep = ({ formData, updateFormData, errors, onPreviewDocument }) => {
  const [showMSMEDeclaration, setShowMSMEDeclaration] = useState(false);

  // Supplier Type removed as per new requirement

  // Updated supplier groups as per requirements
  const supplierGroups = [
    { value: 'oem-customer', label: 'OEM-Customer Referred Supplier' },
    { value: 'odm-amber', label: 'ODM-PICLDeveloped Supplier' }
  ];

  // Updated supplier categories as per requirements
  const supplierCategories = [
    { value: 'rw', label: 'RW-Raw Material' },
    { value: 'pk', label: 'PK-Packaging' },
    { value: 'consumables', label: 'Consumables' },
    { value: 'others', label: 'Others' }
  ];

  // Commodity codes removed - now collected in approval questionnaire

  // MSME categories
  const msmeCategories = [
    { value: 'micro', label: 'Micro' },
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' }
  ];

  // Vendor types for MSME classification
  const vendorTypes = [
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'trading', label: 'Trading' },
    { value: 'service', label: 'Service' }
  ];

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  const handleMSMEChange = (value) => {
    updateFormData({ 
      msmeStatus: value,
      msmeDeclaration: false,
      msmeCertificate: null,
      msmeCategory: '',
      msmeNumber: ''
    });
  };

  const generateMSMEDeclarationPDF = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-GB');
    const formattedTime = currentDate.toLocaleTimeString('en-GB');

    // Create new PDF document
    const doc = new jsPDF();
    
    // Set font styles
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    
    // Header: Company name only (remove person-in-charge name)
    doc.text(`${formData.companyName}`, 20, 30);
    
    // Title
    doc.setFontSize(14);
    doc.text('DECLARATION OF REGISTRATION IN NON MSME', 20, 50);
    
    // Reset font for body text
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    // Updated MSME declaration text
    const declarationText = `We, ${formData.companyName}, a company incorporated under the laws of India and having its registered office at ${formData.registeredAddress}, ${formData.registeredCity}, ${formData.registeredState}, ${formData.registeredCountry}, do hereby declare that:

1. Our company is NOT registered under the Micro, Small and Medium Enterprises Development Act, 2006 (MSME Act) as on the date of this declaration.

2. We understand that MSME registration provides various benefits and incentives under government schemes, but we have chosen not to register under the MSME Act.

3. This declaration is made voluntarily and we are aware of the consequences of not being registered under the MSME Act.

4. We undertake to inform PICL immediately if we register under the MSME Act in the future.`;
    
    // Split text to fit page width and render with dynamic spacing
    const pageHeight = (doc.internal.pageSize.getHeight && doc.internal.pageSize.getHeight()) || doc.internal.pageSize.height;
    const contentX = 20;
    const contentWidth = 170;
    const bodyStartY = 70;
    const splitText = doc.splitTextToSize(declarationText, contentWidth);
    doc.text(splitText, contentX, bodyStartY);
    const bodyTextDims = doc.getTextDimensions(splitText);
    let cursorY = bodyStartY + (bodyTextDims && bodyTextDims.h ? bodyTextDims.h : 0) + 12;

    // Signature section
    doc.text('For and on behalf of', contentX, cursorY);
    cursorY += 10;
    doc.setFont('helvetica', 'bold');
    doc.text(formData.contactPersonName, contentX, cursorY);
    cursorY += 10;
    doc.setFont('helvetica', 'normal');
    doc.text('(Digitally Signed)', contentX, cursorY);
    cursorY += 12;

    // Address
    const addressText = `${formData.registeredAddress}, ${formData.registeredCity}, ${formData.registeredState}, ${formData.registeredCountry}`;
    const splitAddress = doc.splitTextToSize(addressText, contentWidth);
    doc.text(splitAddress, contentX, cursorY);
    const addressDims = doc.getTextDimensions(splitAddress);
    cursorY += (addressDims && addressDims.h ? addressDims.h : 0) + 12;

    // Footer with timestamp (ensure it stays below content and within page)
    doc.setFontSize(10);
    const footerY = Math.min(cursorY + 10, pageHeight - 15);
    doc.text(`Document has been digitally signed with acceptance on PICLCompliance System at ${formattedTime} on ${formattedDate}`, contentX, footerY);
    
    // Prepare filename and Blob for backend upload
    const fileName = `MSME_Declaration_${formData.companyName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    const blob = doc.output('blob');
    const file = new File([blob], fileName, { type: 'application/pdf' });

    // Download for user and store for backend upload on submit
    doc.save(fileName);

    // Update form to indicate declaration was generated and attach file
    updateFormData({ msmeDeclaration: true, msmeDeclarationFile: file });
    setShowMSMEDeclaration(false);
  };

  // Check if the selected country is India
  const isIndianCountry = isIndia(formData.countryOrigin);
  
  // Get currency information based on country
  const currencySymbol = getCurrencySymbol(formData.countryOrigin);
  const currencyName = getCurrencyName(formData.countryOrigin);

  // Build comprehensive currency list (deduplicated by currency code)
  const currencyOptions = React.useMemo(() => {
    const seen = new Set();
    const options = [];
    for (const c of countryCurrencies) {
      if (!seen.has(c.code)) {
        seen.add(c.code);
        options.push({ value: c.code, label: `${c.code} - ${c.name}` });
      }
    }
    // Sort alphabetically by code for easier search
    options.sort((a, b) => a.value.localeCompare(b.value));
    return options;
  }, []);

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Supplier Categorization</h2>
        <p className="text-text-secondary">
          Help us categorize your business to streamline our procurement process.
        </p>
        {!isIndianCountry && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <span className="font-medium">ℹ️ Note:</span> Some fields are hidden for non-Indian vendors as per business requirements.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Supplier Type removed */}

        {/* Supplier Group and Supplier Category fields removed as per requirements */}

            <Select
              label="Preferred Currency"
              description="Select your preferred currency for transactions"
              options={currencyOptions}
              value={formData.preferredCurrency}
              onChange={(value) => handleInputChange('preferredCurrency', value)}
              error={errors.preferredCurrency}
              searchable
              required
            />

        <Input
          label={`Annual Turnover (${currencySymbol})`}
          type="number"
          placeholder={`Enter annual turnover in ${currencyName}`}
          value={formData.annualTurnover}
          onChange={(e) => handleInputChange('annualTurnover', e.target.value)}
          error={errors.annualTurnover}
          description={`Currency: ${currencyName} (${currencySymbol})`}
        />

        {/* Products/Services Offered removed */}
      </div>

      {/* MSME Status Section - Only for Indian vendors */}
      {isIndianCountry && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">MSME Status</h3>
          <p className="text-sm text-text-secondary mb-4">
            Micro, Small & Medium Enterprises (MSME) registration provides various benefits under government schemes.
          </p>

          <div className="space-y-4">
            <Select
              label="MSME Registration Status"
              options={[
                { value: 'registered', label: 'MSME Registered' },
                { value: 'not-registered', label: 'Not MSME Registered' }
              ]}
              value={formData.msmeStatus}
              onChange={handleMSMEChange}
              error={errors.msmeStatus}
              required
            />

            {formData.msmeStatus === 'registered' && (
              <div className="space-y-4">
                <Select
                  label="MSME Category"
                  options={msmeCategories}
                  value={formData.msmeCategory}
                  onChange={(value) => handleInputChange('msmeCategory', value)}
                  error={errors.msmeCategory}
                  required
                />

                <Input
                  label="UDYAM Registration Number"
                  type="text"
                  placeholder="Enter UDYAM registration number (e.g., UDYAM-MH-00-1234567)"
                  value={formData.msmeNumber}
                  onChange={(e) => handleInputChange('msmeNumber', e.target.value)}
                  error={errors.msmeNumber}
                  required
                  description="Format: UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)"
                />

                <Input
                  label="Classification Year"
                  type="number"
                  placeholder="Enter classification year (e.g., 2024)"
                  value={formData.classificationYear}
                  onChange={(e) => handleInputChange('classificationYear', e.target.value)}
                  error={errors.classificationYear}
                  min="2020"
                  max="2030"
                  required
                />

                <Input
                  label="Classification Date"
                  type="date"
                  value={formData.classificationDate}
                  onChange={(e) => handleInputChange('classificationDate', e.target.value)}
                  error={errors.classificationDate}
                  required
                />

                <Select
                  label="Vendor Type"
                  options={vendorTypes}
                  value={formData.vendorType}
                  onChange={(value) => handleInputChange('vendorType', value)}
                  error={errors.vendorType}
                  placeholder="Select vendor type"
                />

                <FileUpload
                  label="MSME Certificate"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={4 * 1024 * 1024}
                  value={formData.msmeCertificate}
                  onChange={(file) => handleFileUpload('msmeCertificate', file)}
                  onRemove={() => handleRemoveFile('msmeCertificate')}
                  onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'MSME_CERTIFICATE')}
                  error={errors.msmeCertificate}
                  required
                  description="Max 4MB - PDF, JPG, or PNG format"
                />
              </div>
            )}

            {formData.msmeStatus === 'not-registered' && (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 mb-3">
                    <strong>Note:</strong> If you are not MSME registered, you need to generate a declaration document.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowMSMEDeclaration(true)}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                  >
                    Generate MSME Declaration
                  </button>
                </div>

                {formData.msmeDeclaration && (
                  <div className="text-sm text-success">
                    ✓ MSME Declaration PDF has been generated and downloaded
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Classifications */}
      {/* Additional Classifications removed as per requirement */}

      {/* MSME Declaration Modal */}
      {showMSMEDeclaration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Generate MSME Declaration</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will generate a declaration document stating that your company is not registered under MSME. 
              The PDF document will be automatically downloaded.
            </p>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={generateMSMEDeclarationPDF}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                Generate & Download
              </button>
              <button
                type="button"
                onClick={() => setShowMSMEDeclaration(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorizationStep;